import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Paper,
  CircularProgress,
  Pagination,
} from "@mui/material";
import {
  Assignment as AssignmentIcon
} from "@mui/icons-material";
import { useLazyGetUserExamsQuery } from "@/store/api/user/examApi";
import { useSelector } from "react-redux";
import { selectIsAdmin } from "@/store/slices/authSlice";
import { useLazyGetAdminExamsQuery } from "@/store/api/adminApi";
import { ExamItem } from "@/components/user/ExamItem";

const MyExaminations = () => {
  const isAdmin = useSelector(selectIsAdmin);

  const [getAdminExams, { data: adminData, isFetching: adminLoading }] = useLazyGetAdminExamsQuery();
  const [getUserExams, { data: userData, isLoading: userLoading }] = useLazyGetUserExamsQuery();

  const examsLoading = isAdmin ? adminLoading : userLoading;
  const limit = 9; // Show more exams on dedicated page

  const [page, setPage] = useState(1);

  const exams = useMemo(() => {
    return isAdmin ? adminData?.data : userData?.data;
  }, [isAdmin, adminData, userData]);

  const pagination = useMemo(() => {
    return isAdmin ? adminData?.pagination : userData?.pagination;
  }, [isAdmin, adminData, userData]);

  useEffect(() => {
    if (isAdmin) {
      getAdminExams({ page, limit, sort: 'title', order: 'asc' });
    } else {
      getUserExams({ page, limit, sort: 'title', order: 'asc' });
    }
  }, [page]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #f0f4f8 0%, #e1e8ed 50%, #e3f2fd 100%)",
        py: 2,
      }}
    >
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box
          sx={{
            mb: 4,
            textAlign: "center",
            p: 4,
            borderRadius: 4,
            background: "white",
            backdropFilter: "blur(20px)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 3 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                background:
                  "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #8e44ad 100%)",
                mr: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AssignmentIcon sx={{ color: "white", fontSize: 32 }} />
            </Box>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 700,
                background:
                  "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #8e44ad 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              TOEIC Mock Tests
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: "#2c3e50",
              fontWeight: 500,
              opacity: 0.8,
            }}
          >
            Track your progress and practice with TOEIC examinations
          </Typography>
        </Box>

        {/* Examinations Section */}
        <Paper
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 4,
            background: "white",
            backdropFilter: "blur(20px)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <Grid container spacing={4}>
            {examsLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  my: 3,
                }}
              >
                <CircularProgress />
              </Box>
            ) : exams?.length > 0 ? (
              exams.map((test) => (
                <Grid item xs={12} sm={6} md={4} key={test._id}>
                  <ExamItem test={test} />
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 3,
                    background: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    py: 8,
                    px: 4,
                    textAlign: "center",
                    transition: "all 0.3s ease",
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        p: 3,
                        mb: 2,
                        borderRadius: "50%",
                        background: "rgba(25, 118, 210, 0.1)",
                        width: 80,
                        height: 80,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                      }}
                    >
                      <AssignmentIcon sx={{ fontSize: 40, color: "#1976D2" }} />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ mb: 2, color: "#546e7a", fontWeight: 600 }}
                    >
                      No examinations available yet
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ maxWidth: 500, mx: "auto", mb: 4 }}
                    >
                      We're preparing new TOEIC examinations for you. Check back
                      soon or explore our available resources.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Pagination */}
          {pagination?.totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Pagination
                count={pagination.totalPages || 1}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default MyExaminations;
